import { html, render } from "lit";
import { Entidad } from "../../../data/Entidad";
import { CFacturaCFDIComplementoConcepto, ICatalogoSATClaveProductServ, ICatalogoSATClaveUnidad } from "../../../data/entidad/Factura";
import { IFinanzaAgrupadorCargos } from "../../../data/entidad/FinanzaAgrupadorCargos";
import { _DICC_ESCUELA, _DICC_FULL_ESCUELAS } from "../../../data/modulo/Escuela";
import { _SvFinanzasObtenerListaAgrupadorCargos } from "../../../data/modulo/FinanzaAgrupadorCargos";
import { _SvFacturaObtenerClaveProdServSATByCargo, _SvFacturaObtenerClaveUnidadSATByCargo } from "../../../data/modulo/FinanzaCargo";
import _L, { _HttpMsg, _HttpMsgV2 } from "../../../util/Labels";
import { Fields, FormGenerator, IControlCreated, IField } from "../../controlD3/Formulario";
import { NotificacionV2 } from "../../controlD3/NotificacionV2";
import { RadioList } from "../../controlD3/RadioList";
import { UIUtilLang } from "../../util/Language";
import { UIUtilPermission } from "../../util/Permission";
import { UIUtilTime } from "../../util/Time";
import { UIUtilViewData } from "../../util/ViewData";
import { UIUtilViewEscuelas } from "../Escuelas/Util";
import { UIUtilViewFinanzaCargo } from "./FinanzaCargo";
import "./FinanzaCargoForm.scss";
import { UIUtilViewFinanzaHorasExtras } from "./FinanzaHorasExtras";

import IFinanzaCargo = Entidad.IFinanzaCargo;
import CTipoValor = Entidad.CFinanzaCargoTipoValor;
import CTipoInteres = Entidad.CFinanzaCargoTipoInteres;
import CTipoTasa = Entidad.CFinanzasCargoTipoTasa;
import CFinanzasCargoRecurrenciaRecargo = Entidad.CFinanzasCargoRecurrenciaRecargo;
import CCategoriaTipoFinanza = Entidad.CFinanzaCargoCategoria
import CPeriodicidadCargos = Entidad.CFinanzaCargoPeriodicidadCargos;
import CAccionPermiso = Entidad.CAccionPermiso;

const INTERVALOBLOQUE_VALOR_NINGUNO = -1 as any
const AGRUPADOR_VALOR_NO_APLICA = 0;

type ICatalogoSATClaveProductServForm = ICatalogoSATClaveProductServ & { full_name: string }

type ICatalogoSATClaveUnidadForm = ICatalogoSATClaveUnidad & { full_name: string }

export type CCargoFormActions = (CAccionPermiso.Agregar | CAccionPermiso.Editar);
export type TCargoFormOrigin = ("FinanzasCargos" | "HorasExtras" | "AlumnosEstadoCuenta");

const langContextModuleFCargo = "finanzascargo";
const MODULO = Entidad.CModulo.FinanzasCargo;

export enum CFinanzaCargoFormField {
    Nombre = "Nombre",
    TipoValor = "TipoValor",
    Valor = "Valor",
    // Modalidad = "Modalidad",
    Periodicidad = "Periodicidad",
    Escuela = "IdEscuela",
    Escuelas = "IdEscuelas",
    TipoRecargo = "TipoRecargo",
    TipoInteres = "TipoInteres",
    ValorRecargo = "ValorRecargo",
    TipoTasa = "TipoTasa",
    IntervaloBloque = "IntervaloBloque",
    IdCicloEscolar = "IdCicloEscolar",
    FechaAplicacion = "FechaAplicacion",
    _SeparadorDatosFiscales = "__Separador",
    IdAgrupador = "IdAgrupador",
    ClaveProdServSAT = "ClaveProdServSAT",
    ClaveUnidadSAT = "ClaveUnidadSAT",
    ComplementoRequerido = "ComplementoRequerido",
}

export type TFinanzaCargoDataForm = IFinanzaCargo & { IdEscuelas?: number[], IdCicloEscolar?: number, FechaAplicacion?: string }

// ***************************************************************************
// CREATE FORM
// ***************************************************************************

export function _GetFinanzaCargoForm(action: (CAccionPermiso.Agregar | CAccionPermiso.Editar), cargoBase?: TFinanzaCargoDataForm, formOrigin: TCargoFormOrigin = "FinanzasCargos", idEscuela?: number): FormGenerator<TFinanzaCargoDataForm> {
    const esEditar = (action == CAccionPermiso.Editar);
    const isEdoCuenta = formOrigin == "AlumnosEstadoCuenta"
    const isHorasExtras = (cargoBase?.Categoria == CCategoriaTipoFinanza.CargoEntrada || cargoBase?.Categoria == CCategoriaTipoFinanza.CargoSalida);
    const escuelasConPermiso = UIUtilPermission._GetSchoolsByActionModule(MODULO, action, cargoBase?.IdEscuela || 0)

    // ***** PARA ALUMNOS EDO.CUENTA *****
    let ciclosEscolares: Entidad.ICicloEscolar[];
    let initCicloEscolar: Entidad.ICicloEscolar;
    let initMin: string;
    let initMax: string;
    let initDate: string;

    const getInitDateDefault = (init: string, fin: string) => {
        let initValidDt = UIUtilTime._GetValidatorDateYMD(new Date(init));
        let endValidDt = UIUtilTime._GetValidatorDateYMD(new Date(fin));
        let nowValidDt = UIUtilTime._GetValidatorDateYMD(new Date());

        if (initValidDt <= nowValidDt && endValidDt >= nowValidDt) {
            return UIUtilTime._FmtToInputDate(new Date(), { removeZ: true })
        } else {
            return UIUtilTime._FmtToInputDate(init, { removeZ: true });
        }
    }

    // ***** PARA ALUMNOS EDO.CUENTA *****
    if (formOrigin == "AlumnosEstadoCuenta") {
        ciclosEscolares = UIUtilViewEscuelas._GetCiclosEscolaresListFromSchool(idEscuela);
        if (!ciclosEscolares.length) {
            NotificacionV2._Mostrar(_L("panelfinanzasedocuenta.notif_nociclosescolares"), "ADVERTENCIA")
            return null;
        }
        initCicloEscolar = UIUtilViewEscuelas._GetCurrentCicloEscolar(ciclosEscolares);
        initMin = initCicloEscolar ? UIUtilTime._FmtToInputDate(initCicloEscolar.FechaInicio, { removeZ: true }) : null;
        initMax = initCicloEscolar ? UIUtilTime._FmtToInputDate(initCicloEscolar.FechaFin, { removeZ: true }) : null;
        initDate = getInitDateDefault(initCicloEscolar.FechaInicio, initCicloEscolar.FechaFin);
    }
    const cargoInit: TFinanzaCargoDataForm = (() => {
        const esNuevo = !cargoBase || !cargoBase.ID;
        if (esNuevo) {
            cargoBase = <TFinanzaCargoDataForm>{
                Categoria: CCategoriaTipoFinanza.Cargo,
                TipoValor: CTipoValor.Monto,
                TipoRecargo: CTipoValor.Ninguno,
                TipoInteres: CTipoInteres.MontoUnico, ValorRecargo: 0,
                Periodicidad: (formOrigin == "AlumnosEstadoCuenta" ? CPeriodicidadCargos.Unico : null),
                IdCicloEscolar: initCicloEscolar?.Id, FechaAplicacion: initDate,
                IntervaloBloque: CFinanzasCargoRecurrenciaRecargo.Diaria,
            }
            if (idEscuela) {
                cargoBase.IdEscuela = idEscuela
                cargoBase.IdEscuelas = [idEscuela];
            }
            else if (escuelasConPermiso.length == 1) {
                cargoBase.IdEscuela = escuelasConPermiso[0].IdKinder;
                cargoBase.IdEscuelas = [escuelasConPermiso[0].IdKinder];
            }
        } else {
            cargoBase.IntervaloBloque = cargoBase.TipoRecargo == CTipoValor.Monto && cargoBase.TipoInteres == CTipoInteres.MontoUnico
                ? INTERVALOBLOQUE_VALOR_NINGUNO
                : cargoBase.IntervaloBloque // DEFINIR RECURRENCUIA COMO INTERVALOBLOQUE_VALOR_NINGUNO CUANDO NO
        }
        return {
            ...cargoBase,
            IdAgrupador: cargoBase?.IdAgrupador || AGRUPADOR_VALOR_NO_APLICA,
            TipoTasa: (cargoBase.TipoRecargo == CTipoValor.Porcentaje && cargoBase.IntervaloBloque == CFinanzasCargoRecurrenciaRecargo.Mensual ? CTipoTasa.Mensual : cargoBase.TipoTasa)
        };
    })()

    // CREACIÓN DEL FORMULARIO

    const form = new FormGenerator<TFinanzaCargoDataForm>();

    // Concepto/Nombre
    const formFieldNombre = GetCustomFieldScheme(CFinanzaCargoFormField.Nombre, form);
    formFieldNombre.inputAttr.disabled = isHorasExtras;
    formFieldNombre.inputAttr.removeBorder = isHorasExtras;

    // Valor
    const formFieldValor = GetCustomFieldScheme(CFinanzaCargoFormField.Valor, form);

    // Periodicidad
    const formFieldPeriodicidad = GetCustomFieldScheme(CFinanzaCargoFormField.Periodicidad, form)
    formFieldPeriodicidad.selectMaterialAttr.disabled = isHorasExtras || isEdoCuenta;
    formFieldPeriodicidad.selectMaterialAttr.removeBorder = isHorasExtras || isEdoCuenta;

    // Escuelas
    const formFieldEscuelas = GetCustomFieldScheme(CFinanzaCargoFormField.Escuelas, form)
    formFieldEscuelas.selectMaterialAttr.disabled = esEditar;
    formFieldEscuelas.selectMaterialAttr.removeBorder = esEditar;
    formFieldEscuelas.selectMaterialAttr.Data = escuelasConPermiso;

    // Escuela
    const formFieldEscuela = GetCustomFieldScheme(CFinanzaCargoFormField.Escuela, form)
    formFieldEscuela.selectMaterialAttr.disabled = esEditar && formOrigin != "HorasExtras";
    formFieldEscuela.selectMaterialAttr.removeBorder = esEditar && formOrigin != "HorasExtras";
    formFieldEscuela.selectMaterialAttr.Data = escuelasConPermiso;
    if (formOrigin == "HorasExtras") {
        formFieldEscuela.selectMaterialAttr.onChange = (idEscuela: number) => {
            let newCargo = UIUtilViewFinanzaHorasExtras._GetFinanzaCargoToHoraExtra(idEscuela, cargoInit.Categoria as (CCategoriaTipoFinanza.CargoEntrada | CCategoriaTipoFinanza.CargoSalida));
            form._AsignaData(newCargo as TFinanzaCargoDataForm);
            // form._ControlsData.get("TipoInteres").selection.select(".itemradio").classed("hide", true); // opción 'Ninguno' oculto
            UIUtilViewFinanzaCargo._ApplyTypeValueToInputValue(newCargo.TipoValor, "valorCargo", form);
            UIUtilViewFinanzaCargo._ApplyTypeValueToInputValue(newCargo.TipoRecargo, "valorRecargo", form);
            ApplyInfoTagTasa(form);
        }
    }

    // Tipo recargo
    const formFieldTipoRecargo = GetCustomFieldScheme(CFinanzaCargoFormField.TipoRecargo, form);
    formFieldTipoRecargo.radioListAttr.OnChange = (data) => {
        console.debug("Vent_FinCargo&Desc ->", data, "radios tipo recargos");
        const esRecargoPorcentaje = data.Id == CTipoValor.Porcentaje;
        (form._ControlsData.get(CFinanzaCargoFormField.IntervaloBloque).instance as RadioList)?._SetValueSelected(CFinanzasCargoRecurrenciaRecargo.Diaria);
        if (!esRecargoPorcentaje) {
            (form._ControlsData.get(CFinanzaCargoFormField.TipoInteres).instance as RadioList)?._SetValueSelected(CTipoInteres.MontoUnico + "");
            (form._ControlsData.get(CFinanzaCargoFormField.TipoTasa).instance as RadioList)?._SetValueSelected(CTipoTasa.NoAplica);

            if (data.Id == CTipoValor.Ninguno) {
                form._ControlsData.get(CFinanzaCargoFormField.ValorRecargo).selection.property("value", 0);
            }
        } else {
            (form._ControlsData.get(CFinanzaCargoFormField.TipoTasa).instance as RadioList)?._SetValueSelected(CTipoTasa.Diaria); // DOTEST: HORAS EXTRAS NO LO INCLUYE
            (form._ControlsData.get(CFinanzaCargoFormField.TipoInteres).instance as RadioList)?._SetValueSelected(CTipoInteres.Simple);
        }
        if (esRecargoPorcentaje || data.Id == CTipoValor.Monto) {
            form._GetModelControl<"radioList">("IntervaloBloque").instance._Refresh()
            if (data.Id == CTipoValor.Monto) {
                form._SetModelValue("IntervaloBloque", INTERVALOBLOQUE_VALOR_NINGUNO)
            }
        }
        UIUtilViewFinanzaCargo._ApplyTypeValueToInputValue(data.Id, "valorRecargo", form);
        ApplyInfoTagTasa(form);
    }

    // Tipo interés
    const formFieldTipoInteres = GetCustomFieldScheme(CFinanzaCargoFormField.TipoInteres, form);
    // formFieldTipoInteres.radioListAttr.OnChange =

    // Tipo valor recargo
    const formFielValorRecargo = GetCustomFieldScheme(CFinanzaCargoFormField.ValorRecargo, form);

    // Intervalo bloque
    const formFieldIntervaloBloque = GetCustomFieldScheme(CFinanzaCargoFormField.IntervaloBloque, form);
    // formFieldIntervaloBloque.radioListAttr.OnChange =

    // TipoTasa
    const formFieldTipoTasa = GetCustomFieldScheme(CFinanzaCargoFormField.TipoTasa, form)
    formFieldTipoTasa.radioListAttr.OnChange = () => {
        ApplyInfoTagTasa(form)
    }

    // ***** PARA ALUMNOS EDO.CUENTA *****
    // IdCicloEscolar
    const formFieldIdCicloEscolar = GetCustomFieldScheme(CFinanzaCargoFormField.IdCicloEscolar, form);
    formFieldIdCicloEscolar.selectMaterialAttr.Data = () => UIUtilViewEscuelas._GetCiclosEscolaresListFromSchool(idEscuela);
    formFieldIdCicloEscolar.selectMaterialAttr.onChange = (id: number, dato: Entidad.ICicloEscolar) => {
        let initMin = UIUtilTime._FmtToInputDate(dato.FechaInicio, { removeZ: true });
        let initMax = UIUtilTime._FmtToInputDate(dato.FechaFin, { removeZ: true });
        form._ControlsData.get("FechaAplicacion").selection
            .attr("min", initMin)
            .attr("max", initMax)
            .property("value", getInitDateDefault(dato.FechaInicio, dato.FechaFin));
    }
    // FechaAplicacion
    const formFieldFechaAplicacion = GetCustomFieldScheme(CFinanzaCargoFormField.FechaAplicacion, form);
    formFieldFechaAplicacion.inputAttr.min = initMin;
    formFieldFechaAplicacion.inputAttr.max = initMax;
    // ***** PARA ALUMNOS EDO.CUENTA *****

    const formSchema: Array<IField<TFinanzaCargoDataForm>> = [];
    if (formOrigin == "AlumnosEstadoCuenta") {
        formSchema.push(formFieldIdCicloEscolar);
        formSchema.push(formFieldFechaAplicacion);
    }
    formSchema.push(formFieldNombre);
    if (!isHorasExtras) formSchema.push(formFieldValor);
    formSchema.push(formFieldPeriodicidad);
    if (formOrigin != "AlumnosEstadoCuenta") {
        if (action == CAccionPermiso.Agregar) formSchema.push(formFieldEscuelas);
        else formSchema.push(formFieldEscuela);
    } else {
        formSchema.push(formFieldEscuela);
    }
    formSchema.push(formFieldTipoRecargo);
    formSchema.push(formFieldTipoInteres);
    formSchema.push(formFielValorRecargo);
    formSchema.push(formFieldIntervaloBloque);
    formSchema.push(formFieldTipoTasa);
    formSchema.push(GetCustomFieldScheme(CFinanzaCargoFormField._SeparadorDatosFiscales, form));
    formSchema.push(GetCustomFieldScheme(CFinanzaCargoFormField.IdAgrupador, form, cargoInit));
    formSchema.push(GetCustomFieldScheme(CFinanzaCargoFormField.ClaveProdServSAT, form, cargoInit));
    formSchema.push(GetCustomFieldScheme(CFinanzaCargoFormField.ClaveUnidadSAT, form, cargoInit));
    formSchema.push(GetCustomFieldScheme(CFinanzaCargoFormField.ComplementoRequerido, form, cargoInit));

    form._Crear({
        LangModuleKeyInContext: langContextModuleFCargo,
        schema: formSchema,
        LabelMaxWidth: 160,
        BuildView: (container, controlsForm, form) => {
            const fnAppendRow = (model: CFinanzaCargoFormField) => {
                let control = controlsForm.get(model as keyof TFinanzaCargoDataForm);
                if (control) {
                    container.append(() => control.row.node())
                }
            }
            if (formOrigin == "AlumnosEstadoCuenta") {
                fnAppendRow(CFinanzaCargoFormField.IdCicloEscolar);
                fnAppendRow(CFinanzaCargoFormField.FechaAplicacion);
            }
            if (formOrigin == "HorasExtras") {
                fnAppendRow(CFinanzaCargoFormField.Escuelas);
                fnAppendRow(CFinanzaCargoFormField.Escuela);
            }
            fnAppendRow(CFinanzaCargoFormField.Nombre);
            fnAppendRow(CFinanzaCargoFormField.Valor);
            fnAppendRow(CFinanzaCargoFormField.Periodicidad);
            if (formOrigin != "HorasExtras" && formOrigin != "AlumnosEstadoCuenta") {
                fnAppendRow(CFinanzaCargoFormField.Escuelas);
                fnAppendRow(CFinanzaCargoFormField.Escuela);
            }
            fnAppendRow(CFinanzaCargoFormField.TipoRecargo);
            fnAppendRow(CFinanzaCargoFormField.TipoInteres);
            fnAppendRow(CFinanzaCargoFormField.ValorRecargo);
            fnAppendRow(CFinanzaCargoFormField.IntervaloBloque);
            fnAppendRow(CFinanzaCargoFormField.TipoTasa);

            fnAppendRow(CFinanzaCargoFormField._SeparadorDatosFiscales);
            fnAppendRow(CFinanzaCargoFormField.IdAgrupador);
            fnAppendRow(CFinanzaCargoFormField.ClaveProdServSAT);
            fnAppendRow(CFinanzaCargoFormField.ClaveUnidadSAT);
            fnAppendRow(CFinanzaCargoFormField.ComplementoRequerido);
        },
        OnAssignData: (cargo, controls, form) => {
            controls.get(CFinanzaCargoFormField.TipoTasa).selection.select(".itemradio").classed("hide", true); // opción TipoTasa 'NoAplica' oculto
            setTimeout(() => {

                form._GetModelControl<"radioList">("IntervaloBloque").instance._Refresh()
                form._SetModelValue("IntervaloBloque", cargo.IntervaloBloque)

                controls.get(CFinanzaCargoFormField.TipoTasa).row.classed("hide", cargo.IntervaloBloque != CFinanzasCargoRecurrenciaRecargo.Diaria);
                UIUtilViewFinanzaCargo._ApplyTypeValueToInputValue(cargo.TipoValor, "valorCargo", form);
                UIUtilViewFinanzaCargo._ApplyTypeValueToInputValue(cargo.TipoRecargo, "valorRecargo", form);
                RefreshTipoTasaItemForm(form)
                EvalFormFacturationVisibility(form);
            });
            // _ApplyInfoTagTasa(form);
        },
    }, cargoInit);

    form._Form.classed("finanza-cargo-form", true);

    return form;
}

// ***************************************************************************
// Generador de esquema de campos para formulario de cargo
// ***************************************************************************

const FinanzaCargoFormDatosFiscalesFields = [
    CFinanzaCargoFormField._SeparadorDatosFiscales,
    CFinanzaCargoFormField.IdAgrupador,
    CFinanzaCargoFormField.ClaveProdServSAT,
    CFinanzaCargoFormField.ClaveUnidadSAT,
    CFinanzaCargoFormField.ComplementoRequerido,
] as const;
export type TFinanzaCargoDatosFiscalesForm = {
    [k in typeof FinanzaCargoFormDatosFiscalesFields[number]]: k extends keyof TFinanzaCargoDataForm ? TFinanzaCargoDataForm[k] : unknown
} & {
    // propiedades que no están en FinanzaCargoFormDatosFiscalesFields; any para no perder la tipificación
    [k in Exclude<keyof TFinanzaCargoDataForm, typeof FinanzaCargoFormDatosFiscalesFields[number]>]?: any
}
export function _FinanzaCargoFormGetDatosFiscalesFields(frm: FormGenerator<TFinanzaCargoDataForm>, cargoInit?: TFinanzaCargoDataForm): IField<TFinanzaCargoDataForm>[] {
    const resultFields: IField<TFinanzaCargoDataForm>[] = [];
    for (const field of FinanzaCargoFormDatosFiscalesFields) {
        const customField = GetCustomFieldScheme(field as any, frm, cargoInit);
        resultFields.push(customField);
    }
    return resultFields;
}

function GetCustomFieldScheme(field: CFinanzaCargoFormField, frm: FormGenerator<TFinanzaCargoDataForm>, cargoInit?: TFinanzaCargoDataForm): IField<TFinanzaCargoDataForm> {
    switch (field) {
        case CFinanzaCargoFormField.Escuela:
            return {
                model: CFinanzaCargoFormField.Escuela,
                type: Fields.selectMaterial,
                labelText: "frm1_esc",
                selectMaterialAttr: {
                    valueMember: "IdKinder", displayMember: "Nombre", required: true,
                    onChange: () => EvalFormFacturationVisibility(frm)
                }
            };
        case CFinanzaCargoFormField.Escuelas:
            return {
                model: CFinanzaCargoFormField.Escuelas,
                type: Fields.selectMaterial,
                labelText: "frm1_esc",
                selectMaterialAttr: {
                    multiselect: true, valueMember: "IdKinder", displayMember: "Nombre", required: true,
                    onChange: () => EvalFormFacturationVisibility(frm),
                },
            }
        case CFinanzaCargoFormField.Nombre:
            return {
                model: CFinanzaCargoFormField.Nombre,
                type: Fields.input,
                inputAttr: { type: "text", required: true },
                labelText: "frm1_nombre"
            };
        case CFinanzaCargoFormField.Valor:
            return {
                model: CFinanzaCargoFormField.Valor,
                type: Fields.input,
                inputAttr: { type: "number", min: 0, required: true, step: ".01" },
                labelText: "frm1_valor",
                onValidate: (val) => {
                    let isValid = true
                    let strDecims = val.toString().split(".")[1];
                    if (strDecims) isValid = !(strDecims.length > 2);
                    if (isValid) {
                        isValid = validaValor(val, frm._Data.TipoValor);
                    }
                    return isValid
                }
            };
        case CFinanzaCargoFormField.Periodicidad:
            return {
                model: CFinanzaCargoFormField.Periodicidad,
                type: Fields.selectMaterial,
                labelText: "frm1_periodic",
                selectMaterialAttr: { Data: UIUtilViewData._GetList_Periodicidad(), valueMember: "Id", displayMember: "Name", required: true },
            };
        case CFinanzaCargoFormField.TipoRecargo:
            return {
                model: CFinanzaCargoFormField.TipoRecargo,
                type: Fields.radioList,
                labelText: "frm1_recarg",
                radioListAttr: {
                    Data: UIUtilViewData._GetList_TipoValor("complete"),
                    ReturnOnlyValueMember: true,
                    ValueMember: "Id", DisplayMember: "Name", required: true
                }
            };
        case CFinanzaCargoFormField.TipoInteres:
            return {
                model: CFinanzaCargoFormField.TipoInteres,
                type: Fields.radioList,
                labelText: "frm1_tipointeres",
                radioListAttr: {
                    ReturnOnlyValueMember: true,
                    ValueMember: "Id", DisplayMember: "Name", required: true,
                    Data: () => {
                        const opcionNA = { Id: Entidad.CFinanzaCargoTipoInteres.MontoUnico, Name: _L("general.ninguno") }
                        const opcionISimple = { Id: Entidad.CFinanzaCargoTipoInteres.Simple, Name: _L("c_tipointeres.simple") }
                        const opcionICompuesto = { Id: Entidad.CFinanzaCargoTipoInteres.Compuesto, Name: _L("c_tipointeres.compuesto") }
                        return [opcionNA, opcionISimple, opcionICompuesto]
                    },
                    OnChange: (data) => {
                        console.debug("Vent_FinCargo&Desc ->", data, "radios tipo interes")
                        // const tipoTasaCtrl = frm._GetModelControl<"radioList">(CFormCargoField.TipoTasa);
                        // const tipoTasaRadioCtrl = tipoTasaCtrl.instance;
                        const esInteresPorcentaje = [CTipoInteres.Simple, CTipoInteres.Compuesto].includes(data.Id);
                        // const esInteresRecDiaria = frm._Data.IntervaloBloque == CFinanzasCargoRecurrenciaRecargo.Diaria;
                        const frmIntervaloBloqueValue = frm._GetModelValue(CFinanzaCargoFormField.IntervaloBloque)

                        if (esInteresPorcentaje && (frmIntervaloBloqueValue == null || frmIntervaloBloqueValue == INTERVALOBLOQUE_VALOR_NINGUNO)) {
                            // tipoTasaCtrl.selection.select(".itemradio").classed("hide", true); // Oculta opcion "No apĺica"
                            // if (frm._Data.TipoTasa == CTipoTasa.NoAplica) {
                            //     tipoTasaRadioCtrl._SetValueSelected(esInteresRecDiaria ? CTipoTasa.Diaria : CTipoTasa.Mensual); // Recurrencia mensual solo tiene Tasa mensual
                            // }
                            frm._SetModelValue(CFinanzaCargoFormField.IntervaloBloque, CFinanzasCargoRecurrenciaRecargo.Diaria)
                        } else if (!esInteresPorcentaje) {
                            // tipoTasaRadioCtrl?._SetValueSelected(CTipoTasa.NoAplica);
                            frm._SetModelValue(CFinanzaCargoFormField.IntervaloBloque, INTERVALOBLOQUE_VALOR_NINGUNO)
                        }
                        // tipoTasaCtrl.row.classed("hide", !(esInteresRecDiaria && esInteresPorcentaje))
                        frm._GetModelControl(CFinanzaCargoFormField.IntervaloBloque).row.classed("hide", !esInteresPorcentaje)
                        RefreshTipoTasaItemForm(frm)
                    }
                },
            };
        case CFinanzaCargoFormField.ValorRecargo:
            return {
                model: CFinanzaCargoFormField.ValorRecargo,
                type: Fields.input,
                inputAttr: { type: "number", min: 0, required: true, step: ".01" },
                labelText: "frm1_cant",
                onValidate: (valor) => {
                    const tipoRecargo = frm._Data.TipoRecargo
                    let isValid = true
                    if (tipoRecargo != CTipoValor.Ninguno) {
                        let strDecims = valor.toString().split(".")[1];
                        if (strDecims) isValid = !(strDecims.length > 2);
                        if (isValid) {
                            isValid = validaValor(valor, tipoRecargo);
                        }
                    } else if (frm._Data.ValorRecargo != 0) {
                        frm._SetModelValue("ValorRecargo", 0);
                    }
                    return isValid
                }
            }
        case CFinanzaCargoFormField.IntervaloBloque:
            return {
                model: CFinanzaCargoFormField.IntervaloBloque,
                type: Fields.radioList,
                labelText: "frm1_recurrencia",
                radioListAttr: {
                    ValueMember: "Id", DisplayMember: "Name", required: true, ReturnOnlyValueMember: true, Direction: "horizontal",
                    Data: () => {
                        const opcionNA = { Id: INTERVALOBLOQUE_VALOR_NINGUNO, Name: _L("general.ninguno") }
                        const opcionDiario = { Id: CFinanzasCargoRecurrenciaRecargo.Diaria, Name: _L("finanzascargo.tag_daily") }
                        const opcionMensual = { Id: CFinanzasCargoRecurrenciaRecargo.Mensual, Name: _L("finanzascargo.tag_monthly_fraction"), }
                        if (frm._GetModelValue("TipoRecargo") == CTipoValor.Monto) {
                            return [opcionNA, opcionDiario]
                        }
                        return [opcionDiario, opcionMensual]
                    },
                    OnChange: (d, id: CFinanzasCargoRecurrenciaRecargo) => {
                        RefreshTipoTasaItemForm(frm)
                    }
                },
            };
        case CFinanzaCargoFormField.TipoTasa:
            return {
                model: CFinanzaCargoFormField.TipoTasa,
                type: Fields.radioList,
                labelText: "frm1_tipotasa",
                onValidate: () => {
                    if ([CTipoInteres.Compuesto, CTipoInteres.Simple].includes(frm._GetModelValue("TipoInteres"))) {
                        if (frm._GetModelValue("TipoTasa") == CTipoTasa.NoAplica) {
                            return false
                        }
                    } else {
                        frm._SetModelValue("TipoTasa", CTipoTasa.NoAplica) // NOTE ???
                        // dataForm["TipoTasa"] = CTipoTasa.NoAplica;
                    }
                    return true
                },
                radioListAttr: {
                    // "tag_daily": "", // FIXME
                    Data: [
                        { Id: CTipoTasa.NoAplica, Name: UIUtilLang._GetUIString(langContextModuleFCargo, "tag_dnaplly") },
                        { Id: CTipoTasa.Diaria, Name: UIUtilLang._GetUIString(langContextModuleFCargo, "tag_daily") },
                        { Id: CTipoTasa.Mensual, Name: UIUtilLang._GetUIString(langContextModuleFCargo, "tag_monthly") } // FIXME ENUM
                    ],
                    ValueMember: "Id", DisplayMember: "Name", required: true, ReturnOnlyValueMember: true,
                },
            }
        case CFinanzaCargoFormField.IdCicloEscolar:
            return {
                model: "IdCicloEscolar",
                type: Fields.selectMaterial,
                labelText: "Ciclo Escolar",
                selectMaterialAttr: {
                    valueMember: "Id", displayMember: "Nombre", required: true
                },
            }
        case CFinanzaCargoFormField.FechaAplicacion:
            return {
                model: "FechaAplicacion",
                type: Fields.input,
                inputAttr: { type: "date", required: true },
                labelText: "Aplicación"
            }
        case CFinanzaCargoFormField._SeparadorDatosFiscales:
            return {
                model: CFinanzaCargoFormField._SeparadorDatosFiscales,
                type: "label",
                labelText: _L("finanzascargo.frm1_cfg_fiscal"),
                onLoad: (control) => control.selection.style("font-weight", "bold"),
            }
        case CFinanzaCargoFormField.ClaveProdServSAT:
            return {
                model: "ClaveProdServSAT",
                type: "selectMaterial",
                labelText: "frm1_claveprodservsat",
                textInfo: _L("finanzascargo.claveprodservsat_info"),
                selectMaterialAttr: {
                    valueMember: "key",
                    displayMember: "full_name",
                    required: true,
                    ShowAndEnableSearchText: true,
                    RecyclerView: true,
                    ListWidth: "450px",
                    OnChangeSearchText_GetDataValueToEval: ({ key, name }: ICatalogoSATClaveProductServForm) => (key + name),
                    Data: cargoInit?.ClaveProdServSAT
                        ? [<ICatalogoSATClaveProductServForm>{ key: cargoInit.ClaveProdServSAT, name: cargoInit.NombreProdServSAT, full_name: _L("general.loading") }] // TEMPORAL MIENTRAS CARGA LA LISTA
                        : [],
                    onChange: (_, d: ICatalogoSATClaveProductServForm) => {
                        frm._DataOrigin.NombreProdServSAT = d?.name || ""
                    }
                }
            }
        case CFinanzaCargoFormField.ClaveUnidadSAT:
            return {
                model: "ClaveUnidadSAT",
                type: "selectMaterial",
                labelText: "frm1_claveunidadsat",
                textInfo: _L("finanzascargo.claveunidadsat_info"),
                selectMaterialAttr: {
                    valueMember: "key",
                    displayMember: "full_name",
                    required: true,
                    ShowAndEnableSearchText: true,
                    RecyclerView: true,
                    ListWidth: "450px",
                    OnChangeSearchText_GetDataValueToEval: ({ key, name }: ICatalogoSATClaveUnidadForm) => (key + name),
                    Data: cargoInit?.ClaveUnidadSAT
                        ? [<ICatalogoSATClaveUnidadForm>{ key: cargoInit.ClaveUnidadSAT, name: cargoInit.NombreUnidadSAT, full_name: _L("general.loading") }] // TEMPORAL MIENTRAS CARGA LA LISTA
                        : [],
                    onChange: (_, d: ICatalogoSATClaveUnidadForm) => {
                        frm._DataOrigin.NombreUnidadSAT = d?.name || "";
                        setTimeout(() => {
                            showMessageOnSelectedClavesSAT(frm);
                        }, 500);
                    }
                }
            }
        case CFinanzaCargoFormField.ComplementoRequerido:
            return {
                model: "ComplementoRequerido",
                type: "input",
                labelText: "frm1_complemento_iedu_requerido",
                textInfo: _L("finanzascargo.frm1_complemento_iedu_requerido_info"),
                inputAttr: { type: "checkbox" },
                onGetFixedValue: (val) => (val === true ? CFacturaCFDIComplementoConcepto.IEDU : CFacturaCFDIComplementoConcepto.NoAplica),
            }
        case CFinanzaCargoFormField.IdAgrupador:
            return {
                model: "IdAgrupador",
                type: "selectMaterial",
                labelText: "frm1_agrupador",
                textInfo: _L("finanzascargo.frm1_agrupador_info"),
                selectMaterialAttr: {
                    valueMember: "Id",
                    displayMember: "Prefix",
                    required: true,
                    Data: cargoInit?.IdAgrupador != null
                        ? [<IFinanzaAgrupadorCargos>{ Id: cargoInit.IdAgrupador, Prefix: _L("general.loading") }] // TEMPORAL MIENTRAS CARGA LA LISTA
                        : [],
                    onChange: (idAgrupadorSelected: number | null) => {
                        EvalFormFacturationVisibility(frm);
                        setTimeout(() => { // Auxiliar para que valide y aplique estilos (caso: si tenia estilo 'requerido' y EvalFormFacturationVisibility le asigna valor, hay que resetear estilos)
                            showMessageOnSelectIdAgrupador(idAgrupadorSelected, frm);
                            setTimeout(() => {
                                showMessageOnSelectedClavesSAT(frm);
                            }, 1000);
                        }, 500);
                    },
                    OnStepItemListUI: (container, datum: IFinanzaAgrupadorCargos) => {
                        const infoCompleta: boolean = (datum.Id > AGRUPADOR_VALOR_NO_APLICA && !!(datum.ProdServSAT || datum.UnidadSAT || datum.ComplementoRequerido));
                        render(html`
                        <div>
                            ${datum.Prefix}
                            ${infoCompleta
                                ? html`
                            <br><i style="font-size:var(--fontsize_me4)"> ${datum.ProdServSAT.join(" - ")}</i>
                            <br><i style="font-size:var(--fontsize_me4)"> ${datum.UnidadSAT.join(" - ")}</i>
                            <br><i style="font-size:var(--fontsize_me4)"> ${_L("finanzascargo.agrupador.complemento_requerido")}: ${UIUtilViewData._GetStr_FacturaCFDIComplementoConcepto(datum.ComplementoRequerido)} </i>
                            ` : null
                            }

                        </div>
                        `, container.node())
                    }
                },
            }
    }
}


// ***************************************************************************
// Funciones auxiliares
// ***************************************************************************

function showMessageOnSelectedClavesSAT(frm: FormGenerator<TFinanzaCargoDataForm>) {
    const claveSelected = frm._GetModelValue("ClaveUnidadSAT") || frm._GetModelValue("ClaveProdServSAT");
    const claveUnidadSATField = frm._GetModelControl("ClaveUnidadSAT");
    const { idsEscuelasSelected, idsEscuelasSelectedUsanFacturacion } = _GetCargoFormEscuelasUsaFacturacion(frm);
    if (!!claveSelected && idsEscuelasSelected.length > idsEscuelasSelectedUsanFacturacion.length) {
        let lblInfo = claveUnidadSATField.controlWrapper.select(".labelInfoMssg");
        if (!lblInfo.node()) {
            lblInfo = claveUnidadSATField.controlWrapper.append("label").classed("labelInfoMssg", true);
        }

        const nombresEscuelas = idsEscuelasSelectedUsanFacturacion.map(id => _DICC_FULL_ESCUELAS.get(id)?.Nombre).join(", ");
        lblInfo.text(_L("finanzascargo.claves_sat_aplica_escuelas", nombresEscuelas));
    } else {
        claveUnidadSATField.controlWrapper.select(".labelInfoMssg").remove();
    }
}

function showMessageOnSelectIdAgrupador(idAgrupadorSelected: number, frm: FormGenerator<TFinanzaCargoDataForm>) {
    frm._ValideItem("ClaveProdServSAT");
    frm._ValideItem("ClaveUnidadSAT");
    frm._ValideItem("ComplementoRequerido");
    const agrupadorField = frm._GetModelControl("IdAgrupador");
    const { idsEscuelasSelected, idsEscuelasSelectedUsanFacturacion } = _GetCargoFormEscuelasUsaFacturacion(frm);
    // Un Agrupador siempre va a una escuela (no a más)
    if (idAgrupadorSelected > 0 && idsEscuelasSelected.length > idsEscuelasSelectedUsanFacturacion.length) {
        let lblInfo = agrupadorField.controlWrapper.select(".labelInfoMssg");
        if (!lblInfo.node()) {
            lblInfo = agrupadorField.controlWrapper.append("label").classed("labelInfoMssg", true);
        }

        const nombresEscuelas = idsEscuelasSelectedUsanFacturacion.map(id => _DICC_FULL_ESCUELAS.get(id)?.Nombre).join(", ");
        lblInfo.text(_L("finanzascargo.agrupador_aplica_escuelas", nombresEscuelas));
    } else {
        agrupadorField.controlWrapper.select(".labelInfoMssg").remove();
    }
}

function validaValor(valor: number, tipoValor: CTipoValor): boolean {
    if (tipoValor == CTipoValor.Porcentaje) {
        return valor > 0 && valor <= 100;
    }
    else if (tipoValor == CTipoValor.Monto) {
        return valor > 0;
    } else {
        return false;
    }
}

function RefreshTipoTasaItemForm(frm: FormGenerator<TFinanzaCargoDataForm>) {
    const esRecDiaria = frm._GetModelValue(CFinanzaCargoFormField.IntervaloBloque) == CFinanzasCargoRecurrenciaRecargo.Diaria;
    const esInteresPorcentaje = [CTipoInteres.Simple, CTipoInteres.Compuesto].includes(frm._GetModelValue("TipoInteres"));
    const esInteresRecDiaria = (frm._GetModelValue("TipoRecargo") == CTipoValor.Porcentaje) && esInteresPorcentaje && esRecDiaria

    const tipoTasaReseterValue = esInteresPorcentaje
        ? (esRecDiaria ? CTipoTasa.Diaria : CTipoTasa.Mensual)
        : CTipoTasa.NoAplica
    // const tipoTasaCurrentValue = frm._GetModelControl("TipoTasa")== CTipoTasa.NoAplica

    frm._GetModelControl(CFinanzaCargoFormField.TipoTasa).row.classed("hide", !esInteresRecDiaria);
    if (esInteresPorcentaje) {
        if (frm._GetModelValue("TipoTasa") == CTipoTasa.NoAplica)
            frm._SetModelValue(CFinanzaCargoFormField.TipoTasa, tipoTasaReseterValue)
        else if (frm._GetModelValue("IntervaloBloque") == CFinanzasCargoRecurrenciaRecargo.Mensual)
            frm._SetModelValue(CFinanzaCargoFormField.TipoTasa, CTipoTasa.Mensual) // Recurrencia mensual solo tiene Tasa mensual
    }
    if (!esInteresRecDiaria) return
    ApplyInfoTagTasa(frm);
}

function ApplyInfoTagTasa<T extends IFinanzaCargo>(form: FormGenerator<T>) {
    let mapStringsInfoTasas = new Map<CTipoTasa, string>([
        [CTipoTasa.NoAplica, ""],
        [CTipoTasa.Diaria, UIUtilLang._GetUIString(langContextModuleFCargo, "tag_dailyrate")],
        [CTipoTasa.Mensual, UIUtilLang._GetUIString(langContextModuleFCargo, "tag_monthlyrate")]
    ]);
    let labelInfoMssg = form._ControlsData.get("TipoTasa").controlWrapper.select(".labelInfoMssg");
    if (!labelInfoMssg.node()) {
        labelInfoMssg = form._ControlsData.get("TipoTasa").controlWrapper.append("label").classed("labelInfoMssg", true).style("font-size", "var(--fontsize_me2)").style("color", "var(--color_app_red1)");
    }
    labelInfoMssg.text(mapStringsInfoTasas.get(form._Data.TipoTasa));
}

/**
 * Obtiene ids de escuelas que usan facturación
 */
export function _GetCargoFormEscuelasUsaFacturacion(frm: FormGenerator<TFinanzaCargoDataForm>): { idsEscuelasSelected: number[], idsEscuelasSelectedUsanFacturacion: number[] } {
    const idsEscuelas: number[] = (() => {
        const d = frm._Data
        if (frm._ControlsData.has("IdEscuela")) {
            return d.IdEscuela ? [d.IdEscuela] : []
        } else if (frm._ControlsData.has("IdEscuelas")) {
            return (d.IdEscuelas || [])
        } else if (frm._DataOrigin?.IdEscuela) {
            return [frm._DataOrigin.IdEscuela]
        }
        return [];
    })()
    const idsEscuelasUsanFacturacion: number[] = idsEscuelas.filter(idEscuela => !!_DICC_ESCUELA.get(idEscuela)?.UsaFactura)
    return { idsEscuelasSelected: idsEscuelas, idsEscuelasSelectedUsanFacturacion: idsEscuelasUsanFacturacion };
}

/**
 * Evalúa la visibilidad de campos relacionados a facturación en el formulario de cargo
 * dependiendo de las escuelas seleccionadas y su configuración de facturación.
 * Agrega listas de opciones a campos de clave producto/servicio y unidad SAT dependiendo del agrupador seleccionado y escuelas seleccionadas.
 * @returns Promise<void> para esperar a que se carguen las listas de opciones si es necesario (Promise all)
 */
export function EvalFormFacturationVisibility(frm: FormGenerator<TFinanzaCargoDataForm>): Promise<void> {
    /** idsEscuelasUsanFacturacion */
    const { idsEscuelasSelectedUsanFacturacion: idsEscuelasUsanFacturacion } = _GetCargoFormEscuelasUsaFacturacion(frm);
    const usaFactura = idsEscuelasUsanFacturacion.length > 0;
    const separadorDatosFiscalesField = frm._ControlsData.get(CFinanzaCargoFormField._SeparadorDatosFiscales as any) as IControlCreated<"label">;
    const idAgrupadorField = frm._ControlsData.get("IdAgrupador") as IControlCreated<"selectMaterial">;
    const claveProdServField = frm._ControlsData.get("ClaveProdServSAT") as IControlCreated<"selectMaterial">; // SelectV2<ICatalogoSATClaveProductServForm, "key", "monoselect">;
    const claveUnidadField = frm._ControlsData.get("ClaveUnidadSAT") as IControlCreated<"selectMaterial">; // SelectV2<ICatalogoSATClaveUnidadForm, "key", "monoselect">;
    const complementoRequeridoField = frm._ControlsData.get("ComplementoRequerido") as IControlCreated<"input">; // SelectV2<ICatalogoSATClaveUnidadForm, "key", "monoselect">;

    separadorDatosFiscalesField.row.classed("hide", !usaFactura);
    idAgrupadorField.row.classed("hide", !usaFactura);
    claveProdServField.row.classed("hide", !usaFactura);
    claveUnidadField.row.classed("hide", !usaFactura);
    complementoRequeridoField.row.classed("hide", !usaFactura);

    if (!usaFactura) {
        return Promise.resolve();
    }
    const idAgrupador = frm._Data.IdAgrupador;
    const idCargo = frm._DataOrigin?.ID;
    // const idEscuela = idsEscuelas.length > 0 ? idsEscuelas[0] : null;

    claveProdServField.instance._disabled = !!idAgrupador;
    claveProdServField.instance._ShowInputBorder(!!idAgrupador);
    claveUnidadField.instance._disabled = !!idAgrupador;
    claveUnidadField.instance._ShowInputBorder(!!idAgrupador);
    complementoRequeridoField.selection.property("disabled", !!idAgrupador);

    const campoAgrupadorDisponible = idsEscuelasUsanFacturacion.length == 1;
    idAgrupadorField.row.classed("hide", !campoAgrupadorDisponible);

    let promiseAgrupadores: Promise<IFinanzaAgrupadorCargos[]> = null;
    if (campoAgrupadorDisponible) {
        promiseAgrupadores = _SvFinanzasObtenerListaAgrupadorCargos(idsEscuelasUsanFacturacion[0])
            .then(r => [
                <IFinanzaAgrupadorCargos>{ Id: AGRUPADOR_VALOR_NO_APLICA, Prefix: _L("general.noaplica_long") },
                ...r.Datos
            ])
        idAgrupadorField.instance._UpdateList(promiseAgrupadores);
    }

    const promiseProdServ = GetDataFromCargoFormAndClavePSVisible(idsEscuelasUsanFacturacion, idCargo);
    const promiseUnidad = GetDataFromCargoFormAndClaveUnitVisible(idsEscuelasUsanFacturacion, idCargo);
    if (idAgrupador) {
        // Carga listas si no han sido cargadas
        claveProdServField.instance._UpdateList(promiseProdServ);
        claveUnidadField.instance._UpdateList(promiseUnidad);

        // Asigna valores de agrupador seleccionado a campos SAT
        if (promiseAgrupadores) {
            promiseAgrupadores.then(agrupadoresList => {
                const agrupadorSeleted: IFinanzaAgrupadorCargos = agrupadoresList.find(a => a.Id == idAgrupador);
                const prodServAgrupador = agrupadorSeleted?.ProdServSAT || [];
                const unidadAgrupador = agrupadorSeleted?.UnidadSAT || [];

                frm._DataOrigin.ClaveProdServSAT = prodServAgrupador[0] || null;
                frm._DataOrigin.NombreProdServSAT = prodServAgrupador[1] || null;
                frm._DataOrigin.ClaveUnidadSAT = unidadAgrupador[0] || null;
                frm._DataOrigin.NombreUnidadSAT = unidadAgrupador[1] || null;
                frm._DataOrigin.ComplementoRequerido = agrupadorSeleted?.ComplementoRequerido || CFacturaCFDIComplementoConcepto.NoAplica;
                frm._SetModelValue("ClaveProdServSAT", prodServAgrupador[0] || null);
                frm._SetModelValue("ClaveUnidadSAT", unidadAgrupador[0] || null);
                frm._SetModelValue("ComplementoRequerido", agrupadorSeleted?.ComplementoRequerido || CFacturaCFDIComplementoConcepto.NoAplica);
            })
        }
    } else {
        // Carga listas de campos SAT
        claveProdServField.instance._UpdateList(promiseProdServ);
        claveUnidadField.instance._UpdateList(promiseUnidad);
    }

    // Retorna promise all
    return Promise.all([promiseAgrupadores, promiseProdServ, promiseUnidad].filter(p => p != null)).then(() => { });
}


async function GetDataFromCargoFormAndClavePSVisible(idsEscuelas: number[], idCargo: number): Promise<ICatalogoSATClaveProductServForm[]> {
    const resultDatos: ICatalogoSATClaveProductServForm[] = [];
    for (const idEscuela of idsEscuelas) {
        const res = await _SvFacturaObtenerClaveProdServSATByCargo(idEscuela, idCargo)
            .then(res => {
                if (res.Resultado == -2) {
                    NotificacionV2._Mostrar(_HttpMsg("factura/base", res.Resultado), "ADVERTENCIA")
                }
                else if (res.Resultado <= 0) {
                    NotificacionV2._Mostrar(_HttpMsgV2(res), "ADVERTENCIA")
                }
                return res;
            })
        if (res.Resultado <= 0 || !res.Datos) {
            return [];
        }
        const dataRes = res.Datos.map((d) => (<ICatalogoSATClaveProductServForm>{ ...d, full_name: `${d.key} - ${d.name}` }))
        if (idEscuela == idsEscuelas[0]) {
            resultDatos.push(...dataRes);
            continue;
        }
        // Aplica union (intersección) de listas
        const dataA = [...resultDatos];
        resultDatos.length = 0; // Limpia array
        for (const a of dataA) {
            if (dataRes.findIndex(b => b.key == a.key) >= 0) {
                resultDatos.push(a);
            }
        }
    }
    return resultDatos;
}

async function GetDataFromCargoFormAndClaveUnitVisible(idsEscuelas: number[], idCargo: number): Promise<ICatalogoSATClaveUnidadForm[]> {
    const resultDatos: ICatalogoSATClaveUnidadForm[] = [];
    for (const idEscuela of idsEscuelas) {
        const res = await _SvFacturaObtenerClaveUnidadSATByCargo(idEscuela, idCargo)
            .then(res => {
                if (res.Resultado == -2) {
                    NotificacionV2._Mostrar(_HttpMsg("factura/base", res.Resultado), "ADVERTENCIA")
                }
                else if (res.Resultado <= 0) {
                    NotificacionV2._Mostrar(_HttpMsgV2(res), "ADVERTENCIA")
                }
                return res;
            });
        if (res.Resultado <= 0 || !res.Datos) {
            return [];
        }
        const dataRes = res.Datos.map((d) => (<ICatalogoSATClaveUnidadForm>{ ...d, full_name: `${d.key} - ${d.name}` }))
        if (idEscuela == idsEscuelas[0]) {
            resultDatos.push(...dataRes);
            continue;
        }
        // Aplica union (intersección) de listas
        const dataA = [...resultDatos];
        resultDatos.length = 0; // Limpia array
        for (const a of dataA) {
            if (dataRes.findIndex(b => b.key == a.key) >= 0) {
                resultDatos.push(a);
            }
        }
    };
    return resultDatos;
}